import { useMemo, useState } from "react";
import BrandsList from "./BrandsList";
import ModelsList from "./ModelsList";
import AddBrandModal from "./AddBrandModal";

function WorkspaceStat({ label, value, accent = false }) {
  return (
    <div
      className={`rounded-2xl border px-4 py-3 ${
        accent
          ? "border-slate-900 bg-slate-900 text-white dark:border-white dark:bg-white dark:text-black"
          : "border-slate-200 bg-white text-slate-900 dark:border-slate-800 dark:bg-slate-900 dark:text-white"
      }`}
    >
      <p
        className={`text-[11px] font-semibold uppercase tracking-[0.14em] ${
          accent
            ? "text-white/70 dark:text-black/70"
            : "text-slate-500 dark:text-slate-400"
        }`}
      >
        {label}
      </p>
      <p className="mt-2 text-lg font-semibold">{value}</p>
    </div>
  );
}

export default function BrandModelSettings() {
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [showAddBrand, setShowAddBrand] = useState(false);
  const [brandReloadKey, setBrandReloadKey] = useState(0);

  const hasSelectedBrand = !!selectedBrand;

  const headerTitle = useMemo(
    () => (hasSelectedBrand ? selectedBrand.name : "Brands & Models"),
    [hasSelectedBrand, selectedBrand]
  );

  const headerDescription = useMemo(
    () =>
      hasSelectedBrand
        ? "Review, organize, and maintain the model catalog registered under this brand."
        : "Maintain the master catalog of tractor brands and their associated models from a structured configuration workspace.",
    [hasSelectedBrand]
  );

  const handleBrandModalClose = () => {
    setShowAddBrand(false);
    setBrandReloadKey((k) => k + 1);
  };

  return (
    <div className="min-h-full space-y-6 text-slate-900 dark:text-slate-100">
      {/* Hero / page header */}
      <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_12px_40px_rgba(15,23,42,0.07)] dark:border-slate-800 dark:bg-slate-900 dark:shadow-[0_12px_40px_rgba(0,0,0,0.3)]">
        <div className="border-b border-slate-200 bg-gradient-to-r from-slate-50 via-white to-slate-50 px-6 py-6 dark:border-slate-800 dark:from-slate-900 dark:via-slate-900 dark:to-slate-950">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
            <div className="min-w-0">
              <div className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-400">
                Catalog Administration
              </div>

              <h1 className="mt-4 truncate text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">
                {headerTitle}
              </h1>

              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500 dark:text-slate-400">
                {headerDescription}
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              {hasSelectedBrand && (
                <button
                  type="button"
                  onClick={() => setSelectedBrand(null)}
                  className="inline-flex items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Clear Selection
                </button>
              )}

              <button
                type="button"
                onClick={() => setShowAddBrand(true)}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-black px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90 dark:bg-white dark:text-black"
              >
                <span className="text-base leading-none">＋</span>
                Add Brand
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 p-6 md:grid-cols-3">
          <WorkspaceStat
            label="Workspace Mode"
            value={hasSelectedBrand ? "Brand Detail" : "Catalog Overview"}
            accent
          />
          <WorkspaceStat
            label="Selection"
            value={hasSelectedBrand ? selectedBrand.name : "No brand selected"}
          />
          <WorkspaceStat
            label="Focus"
            value={hasSelectedBrand ? "Models Management" : "Brand Selection"}
          />
        </div>
      </section>

      {/* Main workspace */}
      <section className="grid min-h-[calc(100vh-290px)] grid-cols-1 gap-6 xl:grid-cols-[280px_minmax(0,1fr)]">
        {/* Left rail */}
        <aside className="min-h-0">
          <div className="sticky top-6 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_10px_35px_rgba(15,23,42,0.06)] dark:border-slate-800 dark:bg-slate-900 dark:shadow-[0_10px_35px_rgba(0,0,0,0.28)]">
            <div className="border-b border-slate-200 px-5 py-5 dark:border-slate-800">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">
                    Brand Directory
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">
                    Browse and select a manufacturer to manage its registered
                    models.
                  </p>
                </div>

                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-base dark:bg-slate-800">
                  🏷️
                </div>
              </div>
            </div>

            <div className="p-3">
              <BrandsList
                selectedBrand={selectedBrand}
                onSelectBrand={setSelectedBrand}
                onAddBrand={() => setShowAddBrand(true)}
                reloadKey={brandReloadKey}
              />
            </div>
          </div>
        </aside>

        {/* Right workspace */}
        <div className="min-h-0">
          <div className="relative flex min-h-[560px] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_10px_35px_rgba(15,23,42,0.06)] dark:border-slate-800 dark:bg-slate-900 dark:shadow-[0_10px_35px_rgba(0,0,0,0.28)]">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-slate-900 via-slate-700 to-slate-500 dark:from-white dark:via-slate-300 dark:to-slate-500" />

            {!hasSelectedBrand ? (
              <div className="flex flex-1 flex-col items-center justify-center px-6 py-16 text-center">
                <div className="mb-6 flex h-24 w-24 items-center justify-center rounded-[28px] border border-slate-200 bg-slate-50 shadow-sm dark:border-slate-800 dark:bg-slate-800/70">
                  <span className="text-4xl">🚜</span>
                </div>

                <div className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400">
                  Awaiting Selection
                </div>

                <h3 className="mt-5 text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
                  Select a brand to open the workspace
                </h3>

                <p className="mt-3 max-w-md text-sm leading-6 text-slate-500 dark:text-slate-400">
                  Use the brand directory on the left to open a focused model
                  management workspace for a specific manufacturer.
                </p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => setShowAddBrand(true)}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-black px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90 dark:bg-white dark:text-black"
                  >
                    <span className="text-base leading-none">＋</span>
                    Create Brand
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Workspace header */}
                <div className="border-b border-slate-200 px-6 py-5 dark:border-slate-800">
                  <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                    <div className="min-w-0">
                      <div className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400">
                        Brand Workspace
                      </div>

                      <h2 className="mt-4 truncate text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">
                        {selectedBrand.name}
                      </h2>

                      <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500 dark:text-slate-400">
                        Manage the model catalog, keep naming clean, and maintain
                        a consistent structure for this manufacturer.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-800 dark:bg-slate-950/30">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                          Active Brand
                        </p>
                        <p className="mt-1 font-medium text-slate-900 dark:text-white">
                          {selectedBrand.name}
                        </p>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-800 dark:bg-slate-950/30">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                          Workspace Focus
                        </p>
                        <p className="mt-1 font-medium text-slate-900 dark:text-white">
                          Model Catalog
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Models area */}
                <div className="min-h-0 flex-1 p-6">
                  <ModelsList brand={selectedBrand} />
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {showAddBrand && <AddBrandModal onClose={handleBrandModalClose} />}
    </div>
  );
}